import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, Check, Moon, MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);

  useEffect(() => { api.get(`/packages/${id}`).then(({ data }) => setPkg(data)).catch(() => {}); }, [id]);

  if (!pkg) return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-4"><Skeleton className="h-96 rounded-3xl" /></div>;

  const book = () => navigate("/checkout", { state: { item: {
    type: "package", title: pkg.title, amount: pkg.price, currency: "USD",
    details: { destination: pkg.destination, country: pkg.country, nights: pkg.nights, includes: pkg.includes, image: pkg.image },
  } } });

  return (
    <div>
      <div className="relative h-[440px]">
        <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
            <span className="px-3 py-1 rounded-full bg-coral text-white text-xs font-bold">{pkg.tag}</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mt-3 max-w-3xl">{pkg.title}</h1>
            <div className="flex items-center gap-4 text-white/90 mt-3">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{pkg.destination}, {pkg.country}</span>
              <span className="flex items-center gap-1.5"><Moon className="w-4 h-4" />{pkg.nights} nights</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />{pkg.rating}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          <h2 className="font-heading font-bold text-2xl text-slate-950">About this holiday</h2>
          <p className="text-slate-600 leading-relaxed mt-3 text-lg">{pkg.description}</p>
          <h2 className="font-heading font-bold text-2xl text-slate-950 mt-8">What's included</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {pkg.includes.map((inc) => (
              <div key={inc} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
                <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-emerald-600" /></div>
                <span className="text-slate-700 font-medium">{inc}</span>
              </div>
            ))}
          </div>
        </div>
        <aside>
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-6">
            <span className="text-sm text-slate-400">Total price from</span>
            <div className="font-heading font-black text-4xl text-slate-950">{money(pkg.price)}<span className="text-base font-normal text-slate-400"> pp</span></div>
            <div className="text-sm text-slate-500 mt-1">Based on 2 adults sharing</div>
            <Button onClick={book} className="w-full mt-5 rounded-full bg-coral hover:bg-coral-hover h-12 font-heading font-bold gap-2" data-testid="book-package">Book this holiday <ArrowRight className="w-4 h-4" /></Button>
            <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3"><ShieldCheck className="w-4 h-4" />Secure payment & fully refundable</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
